import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { apiError, http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Sparkles, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login, demoLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoCreds, setDemoCreds] = useState(null);

  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  useEffect(() => {
    http.get("/auth/demo-credentials").then((r) => setDemoCreds(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const tryDemo = async () => {
    setDemoLoading(true);
    try {
      await demoLogin();
      toast.success("Signed in to demo account");
      nav("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <AuthShell>
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0B132B]">Sign in</h2>
      <p className="text-slate-500 text-sm mt-1.5 mb-6">Access your WiseAce Invoicing workspace.</p>

      <Button
        onClick={tryDemo}
        disabled={demoLoading}
        data-testid="try-demo-button"
        className="w-full bg-[#D4AF37] hover:bg-[#B89628] text-[#0B132B] font-semibold h-11 mb-4"
      >
        {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Try the Demo Account</>}
      </Button>
      {demoCreds && (
        <div className="mb-6 text-xs bg-[#F1F5F9] border border-slate-200 rounded-lg p-3 text-slate-600" data-testid="demo-credentials">
          <span className="font-semibold text-[#0B132B]">Demo login:</span> {demoCreds.email} · <span className="font-mono">{demoCreds.password}</span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <div className="h-px bg-slate-200 flex-1" />
        <span className="text-xs text-slate-400 uppercase tracking-wider">or with your account</span>
        <div className="h-px bg-slate-200 flex-1" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email address</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.co.za" className="mt-1.5 h-11" data-testid="login-email-input" />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-[#0B132B] font-medium hover:text-[#D4AF37]" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <div className="relative mt-1.5">
            <Input id="password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="h-11 pr-10" data-testid="login-password-input" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3 text-slate-400" data-testid="toggle-password">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit-button"
          className="w-full bg-[#0B132B] hover:bg-[#1C2541] h-11 font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>

      <p className="text-sm text-slate-500 mt-6 text-center">
        Don't have an account?{" "}
        <Link to="/register" className="text-[#0B132B] font-semibold hover:text-[#D4AF37]" data-testid="go-register-link">Create one</Link>
      </p>
    </AuthShell>
  );
}
